import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { FakeLink } from '@/core/FakeLink';
import { SUPPORT_LINK } from '@/utilities/externalLinks';
import { getSourceManagedItemName, getSourceManagedItemOpenLink } from '@/utilities/sourceManagedItem.utilities';
import { faQuestionCircle } from '@seeqdev/icons/fa-sharp-regular-svg-icons';
import { ExternalLink, Icon } from '@seeqdev/qomponents';
import type { SourceManagedItem } from '@/utilities/sourceManagedItem.types';

export type ItemScopeProps = {
  availableOutsideAnalysis: boolean;
  scopeMessage: ScopeMessage;
  canManage: boolean;
  openScopeModal: () => void;
  isLocked?: boolean;
  sourceTable?: SourceManagedItem;
  openSourceTable?: () => void;
};

export interface ScopeMessage {
  translationString: string;
  translationParameter?: Record<string, string>;
}

export const ItemScope = ({
  availableOutsideAnalysis,
  scopeMessage,
  canManage,
  openScopeModal,
  isLocked = false,
  sourceTable,
  openSourceTable,
}: ItemScopeProps) => {
  const { t } = useTranslation();
  const sourceTableName = getSourceManagedItemName(sourceTable);
  const isSourceManaged = !!sourceTableName;
  const openSourceTableLink = getSourceManagedItemOpenLink(sourceTable, openSourceTable);
  const canChangeScope = !availableOutsideAnalysis && canManage && !isLocked && !isSourceManaged;

  return (
    <div className="flexRowContainer tw:mb-sq-5" data-testid="itemScope">
      <div className="flexColumnContainer flexFill">
        <label className="tw:mb-0">{t('PROPERTIES.SCOPE')}</label>
        <ExternalLink href={`${SUPPORT_LINK}/authorization`} variant="icon" extraClassNames="ml5">
          <Icon
            icon={faQuestionCircle}
            tooltip={t('SCOPE.TOOLTIP')}
            tooltipPlacement="right"
            extraClassNames="text-interactive"
          />
        </ExternalLink>
      </div>
      <div className="flexColumnContainer tw:mt-0 tw:mb-0 tw:ml-sq-5">
        <span
          className={clsx('specScopeText', {
            'tw:italic': availableOutsideAnalysis,
          })}>
          {t(scopeMessage.translationString, { ...scopeMessage.translationParameter })}
        </span>
        {isSourceManaged && (
          <div className="tw:ml-sq-5 tw:flex tw:flex-col tw:items-start" data-testid="sourceManagedScope">
            <span>{t('SCOPE.SOURCE_TABLE_MANAGED', { tableName: sourceTableName })}</span>
            {openSourceTableLink && (
              <FakeLink
                onClick={openSourceTableLink}
                testId="scopeSourceTableLink"
                tooltipText="SCOPE.OPEN_SOURCE_TABLE_TOOLTIP"
                extraClassNames="link-underline-hover">
                {t('SCOPE.OPEN_SOURCE_TABLE')}
              </FakeLink>
            )}
          </div>
        )}
        {canChangeScope && (
          <div
            className="specScopeLink sq-text-primary link-underline-hover cursorPointer tw:ml-sq-5"
            data-testid="scopeChange"
            onClick={openScopeModal}>
            {t('SCOPE.CHANGE')}
          </div>
        )}
      </div>
    </div>
  );
};
